import { useState } from "react";
import dayjs from "dayjs";
import { db, eq } from "../lib/api";
import { invalidate, useQuery } from "../lib/hooks";
import { useRefData } from "../lib/refdata";
import { Field, Loading, Sheet, useToast } from "./ui";
import { SolutionsEditor, type Sol } from "./OppSheet";

type Opp = { id: string; full_name?: string; board: string; stage: string; next_action: string | null; next_action_on: string | null; solutions?: Sol[] | null; people?: { full_name: string } | null };

/** A working day `n` days from today (weekends move to Monday). */
export const workday = (n: number) => {
  let d = dayjs().add(n, "day");
  if (d.day() === 6) d = d.add(2, "day");
  if (d.day() === 0) d = d.add(1, "day");
  return d.format("YYYY-MM-DD");
};

/** The suggested next step for a stage: from Settings, else a sensible default by the stage's name. */
export function useSuggestNext() {
  const ref = useRefData();
  const map = ref.data?.setting("pipeline_next_steps") ?? {};
  return (board: string, stage: string): { action: string; days: number } => {
    const s = map[`${board}|${stage}`];
    if (s?.action) return { action: s.action, days: Number(s.days ?? 3) };
    const t = stage.toLowerCase();
    if (/decision|pending/.test(t)) return { action: "Follow up on their decision", days: 3 };
    if (/presented|proposal|held/.test(t)) return { action: "Follow up on the proposal", days: 2 };
    if (/fna|discovery/.test(t)) return { action: "Prepare and present the solution", days: 5 };
    if (/submitted|underwriting/.test(t)) return { action: "Check underwriting status", days: 7 };
    if (/licens/.test(t)) return { action: "Check on licence progress", days: 7 };
    if (/join/.test(t)) return { action: "Start Fast Start and licensing", days: 3 };
    return { action: "Follow up", days: 3 };
  };
}

/** "What's next, and when?": used when a card moves stage, and from the Command Center. */
export function NextStepSheet({ oppId, newStage, onClose, onSaved }: { oppId: string; newStage?: string; onClose: () => void; onSaved?: () => void }) {
  const toast = useToast();
  const suggest = useSuggestNext();
  const q = useQuery(`nextstep:${oppId}`, () => db.one<Opp>("opportunities", { id: eq(oppId), select: "id,person_id,board,stage,next_action,next_action_on,solutions,people(full_name)" }), { staleMs: 0 });
  const o = q.data;
  const stage = newStage ?? o?.stage ?? "";
  const sug = o ? suggest(o.board, stage) : null;
  const [action, setAction] = useState<string | null>(null);
  const [on, setOn] = useState<string | null>(null);
  const [err, setErr] = useState("");
  // Moving a sale to "Submitted": record what was applied for, on the same screen
  const applying = !!o && o.board === "Solution" && !!newStage && /submitted|underwriting/i.test(newStage);
  const [sols, setSols] = useState<Sol[] | null>(null);
  const solutions = sols ?? (o?.solutions ?? []).map((x) => (!x.status || x.status === "Proposed" ? { ...x, status: "Submitted" } : x));
  const a = action ?? (newStage ? sug?.action : o?.next_action || sug?.action) ?? "";
  const d = on ?? (newStage ? (sug ? workday(sug.days) : "") : o?.next_action_on && o.next_action_on >= dayjs().format("YYYY-MM-DD") ? o.next_action_on : sug ? workday(sug.days) : "");
  async function save(withStep = true) {
    setErr("");
    try {
      if (withStep && (!a.trim() || !d)) throw new Error("Add the next action and a date");
      if (applying && !solutions.some((x) => x.solution_type)) throw new Error("Add what they applied for (solution and provider), so it shows on their file.");
      await db.update("opportunities", { id: eq(oppId) }, { ...(newStage ? { stage: newStage } : {}), ...(withStep ? { next_action: a.trim(), next_action_on: d } : {}),
        ...(applying ? { solutions: solutions.filter((x) => x.solution_type) } : {}) });
      if (applying) { invalidate("clients"); invalidate("client"); }
      invalidate("opps"); invalidate("pipe"); invalidate("cc"); invalidate("today"); invalidate("person:");
      toast(newStage ? `${o?.people?.full_name ?? "Deal"} moved to ${newStage}${withStep ? ` · next: ${a} ${dayjs(d).format("ddd MMM D")}` : ""}` : "Next step set");
      onSaved?.(); onClose();
    } catch (e) { setErr((e as Error).message); }
  }
  const quick = [[1, "Tomorrow"], [2, "In 2 days"], [3, "In 3 days"], [7, "Next week"], [14, "In 2 weeks"]] as const;
  return (
    <Sheet title={`${o?.people?.full_name ?? "Deal"}: what's next?`} onClose={onClose} footer={<>
      {newStage && <button className="btn ghost" onClick={() => save(false)}>Move without a next step</button>}
      <button className="btn primary" onClick={() => save(true)}>{newStage ? "Move and set next step" : "Save next step"}</button>
    </>}>
      {!o ? <Loading rows={3} /> : <div className="stack">
        <p className="small muted" style={{ margin: 0 }}>{newStage ? <>Moving from <b>{o.stage}</b> to <b>{newStage}</b>.</> : <>Stage: <b>{o.stage}</b>.</>} Every open deal needs a next action and a date, so it never gets forgotten.</p>
        <Field label="Next action"><input className="input" value={a} onChange={(e) => setAction(e.target.value)} autoFocus /></Field>
        {sug && a !== sug.action && <button className="linkish small" style={{ justifySelf: "start" }} onClick={() => { setAction(sug.action); setOn(workday(sug.days)); }}>Use the suggestion: {sug.action} in {sug.days} days</button>}
        <Field label="When"><input className="input" type="date" value={d} min={dayjs().format("YYYY-MM-DD")} onChange={(e) => setOn(e.target.value)} style={{ maxWidth: 200 }} /></Field>
        <div className="toolbar" style={{ margin: 0 }}>{quick.map(([n, l]) => <button key={n} className="btn small" onClick={() => setOn(workday(n))}>{l}</button>)}</div>
        {applying && <SolutionsEditor personId={(o as any)?.person_id} value={solutions} onChange={setSols} defaultStatus="Submitted" />}
        {err && <div className="error-box" role="alert">{err}</div>}
      </div>}
    </Sheet>
  );
}
